import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Table, Card, Tag, Typography, DatePicker, Select, Button, Input, Alert } from 'antd'
import dayjs from 'dayjs'
import { getOrders, exportOrders } from '../../services/order'
import { formatTimestamp } from '../../utils/format'
import { formatAmount, formatDisplayExchangeRate } from '../../utils/orderDisplay'
import { orderDisplayStatus, orderStatusColors, orderStatusLabels, shouldShowOrderFees } from '../../utils/orderDisplayStatus'
import type { Order, Pagination } from '../../types'

const { Title } = Typography
const { RangePicker } = DatePicker

const TEXT_FILTER_DEBOUNCE_MS = 300

const paymentMethodLabels: Record<string, string> = {
  CREDIT_CARD: '信用卡',
  APPLE_PAY: 'Apple Pay',
  GOOGLE_PAY: 'Google Pay',
  PAYPAL: 'PayPal',
  WECHAT_PAY: '微信支付',
  ALIPAY: '支付寶',
  ALIPAY_HK: 'AlipayHK',
  BOOST: 'Boost',
  TOUCH_N_GO_EWALLET: 'Touch \'n Go',
  LINE_PAY: 'LINE Pay',
  KREDIVO: 'Kredivo',
  GCASH: 'GCash',
  JKOPAY: 'JKOPay',
  PAYPAY: 'PayPay',
  MERPAY: 'Merpay',
  AUPAY: 'au PAY',
  MIR: 'Mir',
  SBP: 'SBP',
  T_PAY: 'T-Pay',
  SBER_PAY: 'Sber Pay',
  YOO_MONEY: 'YooMoney',
  KAKAO_PAY: 'Kakao Pay',
  NAVER_PAY: 'Naver Pay',
  TOSS_PAY: 'Toss Pay',
  PAYCO: 'PAYCO',
  SAMSUNG_PAY: 'Samsung Pay',
  T_MONEY: 'T-money',
  PAYNOW: 'PayNow',
  QRIS: 'QRIS',
  OVO: 'OVO SNAP',
  DANA: 'DANA',
  SHOPEE_PAY: 'ShopeePay',
  VIRTUAL_ACCOUNT: 'Virtual Account / 銀行轉賬',
  CRYPTO: '加密貨幣',
  USDT_DIRECT: 'USDT Direct'
}

export default function OrderList() {
  const [orders, setOrders] = useState<Order[]>([])
  const [pagination, setPagination] = useState<Pagination>({ page: 1, size: 20, total: 0, totalPages: 0 })
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [status, setStatus] = useState<string | undefined>()
  const [orderIdInput, setOrderIdInput] = useState('')
  const [orderId, setOrderId] = useState<string | undefined>()
  const [checkoutIdInput, setCheckoutIdInput] = useState('')
  const [checkoutId, setCheckoutId] = useState<string | undefined>()
  const [customerEmailInput, setCustomerEmailInput] = useState('')
  const [customerEmail, setCustomerEmail] = useState<string | undefined>()
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null])
  const [exporting, setExporting] = useState(false)
  const [pageSize, setPageSize] = useState(20)
  const navigate = useNavigate()

  const fetchOrders = (page: number = 1, size: number = pageSize) => {
    setLoading(true)
    setLoadError(false)
    const params: Parameters<typeof getOrders>[0] = { page, size, status, orderId, checkoutId, customerEmail }
    if (dateRange[0] && dateRange[1]) {
      params.startDate = dateRange[0].valueOf()
      params.endDate = dateRange[1].endOf('day').valueOf()
    }
    getOrders(params)
      .then((res) => {
        setOrders(res.data.items)
        setPagination(res.data.pagination)
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
  }

  const handleExport = async () => {
    if (!dateRange[0] || !dateRange[1]) return
    setExporting(true)
    try {
      const blob = await exportOrders(dateRange[0].valueOf(), dateRange[1].endOf('day').valueOf())
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `orders_${dateRange[0].format('YYYY-MM-DD')}_${dateRange[1].format('YYYY-MM-DD')}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setExporting(false)
    }
  }

  const applyOrderId = (value: string) => {
    setOrderId(value.trim() || undefined)
  }

  const applyCheckoutId = (value: string) => {
    setCheckoutId(value.trim() || undefined)
  }

  const applyCustomerEmail = (value: string) => {
    setCustomerEmail(value.trim() || undefined)
  }

  const handleReset = () => {
    setOrderIdInput('')
    setOrderId(undefined)
    setCheckoutIdInput('')
    setCheckoutId(undefined)
    setCustomerEmailInput('')
    setCustomerEmail(undefined)
    setStatus(undefined)
    setDateRange([null, null])
  }

  useEffect(() => {
    const value = orderIdInput.trim()
    if (!value) {
      setOrderId(undefined)
      return
    }

    const timer = window.setTimeout(() => {
      setOrderId(value)
    }, TEXT_FILTER_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [orderIdInput])

  useEffect(() => {
    const value = checkoutIdInput.trim()
    if (!value) {
      setCheckoutId(undefined)
      return
    }

    const timer = window.setTimeout(() => {
      setCheckoutId(value)
    }, TEXT_FILTER_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [checkoutIdInput])

  useEffect(() => {
    const value = customerEmailInput.trim()
    if (!value) {
      setCustomerEmail(undefined)
      return
    }

    const timer = window.setTimeout(() => {
      setCustomerEmail(value)
    }, TEXT_FILTER_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [customerEmailInput])

  useEffect(() => {
    fetchOrders(1)
  }, [status, dateRange, orderId, checkoutId, customerEmail])

  const computeFee = (record: Order) =>
    record.platformFeeUsd
      ? `${formatAmount(record.platformFeeUsd)} USD`
      : `${formatAmount(record.platformFee)} ${record.currency}`

  const columns = [
    {
      title: '订单号 / Checkout ID',
      key: 'id',
      width: 200,
      render: (_: unknown, record: Order) => (
        <div>
          <div>{record.id}</div>
          <Typography.Text type="secondary" style={{ display: 'block' }}>
            {record.checkoutSessionId || '-'}
          </Typography.Text>
        </div>
      ),
    },
    { 
      title: '產品名稱', 
      key: 'productSnapshot.name', 
      width: 200,
      render: (_: unknown, record: Order) => (record.productSnapshot?.name || '-')
    },
    {
      title: '客戶郵箱',
      dataIndex: 'customerEmail',
      key: 'customerEmail',
      render: (email: string | null) => email || '-'
    },
    {
      title: '支付方式',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (method: string) => paymentMethodLabels[method] || method
    },
    {
      title: '訂單金額',
      key: 'amount',
      render: (_: unknown, record: Order) => `${formatAmount(record.amount)} ${record.currency}`
    },
    {
      title: '匯率',
      key: 'exchangeRate',
      render: (_: unknown, record: Order) =>
        formatDisplayExchangeRate(record.currency, record.exchangeRate)
    },
    {
      title: '手續費',
      key: 'fee',
      render: (_: unknown, record: Order) => shouldShowOrderFees(record.status) ? computeFee(record) : '-'
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (_: string, order: Order) => {
        const displayStatus = orderDisplayStatus(order.status, order.refundStatus)
        return (
          <Tag color={orderStatusColors[displayStatus]}>
            {orderStatusLabels[displayStatus] || displayStatus}
          </Tag>
        )
      }
    },
    {
      title: '創建時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (ts: number) => formatTimestamp(ts)
    },
  ]

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>訂單管理</Title>
      {loadError && (
        <Alert
          type="error"
          showIcon
          message="訂單載入失敗"
          action={<Button size="small" onClick={() => fetchOrders(pagination.page)}>重新載入</Button>}
          style={{ marginBottom: 16 }}
        />
      )}
      <Card>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16, width: '100%' }}>
          <Input
            placeholder="輸入訂單 ID"
            allowClear
            style={{ flex: '1 1 280px', maxWidth: 320, minWidth: 0 }}
            value={orderIdInput}
            onChange={(event) => setOrderIdInput(event.target.value)}
            onPressEnter={() => applyOrderId(orderIdInput)}
          />
          <Input
            placeholder="輸入 Checkout ID"
            allowClear
            style={{ flex: '1 1 280px', maxWidth: 320, minWidth: 0 }}
            value={checkoutIdInput}
            onChange={(event) => setCheckoutIdInput(event.target.value)}
            onPressEnter={() => applyCheckoutId(checkoutIdInput)}
          />
          <Input
            placeholder="輸入客戶郵箱"
            allowClear
            style={{ flex: '1 1 280px', maxWidth: 320, minWidth: 0 }}
            value={customerEmailInput}
            onChange={(event) => setCustomerEmailInput(event.target.value)}
            onPressEnter={() => applyCustomerEmail(customerEmailInput)}
          />
          <Select
            placeholder="訂單狀態"
            allowClear
            style={{ width: 150, maxWidth: '100%' }}
            value={status}
            onChange={setStatus}
            options={[
              { value: 'PENDING', label: '待支付' },
              { value: 'PAID', label: '已支付' },
              { value: 'SETTLED', label: '已結算' },
              { value: 'REFUNDED', label: '已退款' },
              { value: 'DISPUTED', label: '爭議中' },
              { value: 'CLOSED', label: orderStatusLabels.CLOSED },
              { value: 'FAILED', label: orderStatusLabels.FAILED },
            ]}
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates || [null, null])}
            style={{ maxWidth: '100%' }}
          />
          <Button onClick={handleExport} loading={exporting} disabled={!dateRange[0] || !dateRange[1]}>
            導出
          </Button>
          <Button onClick={handleReset} aria-label="重置">
            重置
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100 }}
          onRow={(record) => ({
            onClick: () => navigate(`/dashboard/orders/${record.id}`),
            style: { cursor: 'pointer' }
          })}
          pagination={{
            current: pagination.page,
            pageSize: pagination.size,
            total: pagination.total,
            onChange: (page, size) => {
              if (size !== pageSize) {
                setPageSize(size)
                fetchOrders(1, size)
              } else {
                fetchOrders(page, size)
              }
            },
          }}
        />
      </Card>
    </div>
  )
}
